import _trimEnd from 'lodash-es/trimEnd';

import type { ThemeFontWeight } from '@rover/kibble/styles/themeTypes';
import { IconColorEnum, TextColorEnum } from '@rover/rsdk/src/apiClient/latest/model';
import { isProdNodeEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

export function getNativeStyleFromArray(
  value: number | string | [string, string] | [string, string, string]
): string | number {
  if (!Array.isArray(value)) {
    return value;
  }
  return value[0];
}

export function pxToNumber(value: string | number): number {
  if (typeof value === 'number') return value;
  if (!value || !value.endsWith('px')) return 0;
  return parseInt(_trimEnd(value, 'px'), 10);
}

export type AvertaFontFamilyNames = 'Averta-Regular' | 'Averta-Semibold';
const convertFontWeight = (weight?: ThemeFontWeight): AvertaFontFamilyNames => {
  if (weight === 'semibold') {
    return 'Averta-Semibold';
  }
  return 'Averta-Regular';
};
export function fontWeightToFontFamily(
  weight?: ThemeFontWeight | ThemeFontWeight[]
): AvertaFontFamilyNames | AvertaFontFamilyNames[] {
  if (Array.isArray(weight)) {
    return weight.map(convertFontWeight);
  }
  return convertFontWeight(weight);
}
/**
 * Note: TextColorEnum and IconColorEnum are identical enums but exist as separate types
 * due to how they are generated from the API schema.
 * In the backend, both are mapped to the TextColor enum from the thin API.
 * This allows the getApiColor function to accept both types while maintaining type safety.
 */
export function getApiColor(color: TextColorEnum | IconColorEnum, theme: RoverTheme): string {
  if (!color) {
    Logger.warn(
      `getApiColor called with empty color: ${color}. Returning primary color as fallback.`
    );
    return theme.textColor.primary.toString();
  }
  switch (color) {
    case TextColorEnum.TEXT_COLOR_PRIMARY:
      return theme.textColor.primary.toString();
    case TextColorEnum.TEXT_COLOR_PRIMARY_INVERSE:
      return theme.textColor.primary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY:
      return theme.textColor.secondary.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY_INVERSE:
      return theme.textColor.secondary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY:
      return theme.textColor.tertiary.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY_INVERSE:
      return theme.textColor.tertiary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_PLACEHOLDER:
      return theme.textColor.placeholder.toString();
    case TextColorEnum.TEXT_COLOR_DISABLED:
      return theme.textColor.disabled.toString();
    case TextColorEnum.TEXT_COLOR_PRICE:
      return theme.textColor.price.toString();
    case TextColorEnum.TEXT_COLOR_ERROR:
      return theme.textColor.error.toString();
    case TextColorEnum.TEXT_COLOR_CAUTION:
      return theme.textColor.caution.toString();
    case TextColorEnum.TEXT_COLOR_SUCCESS:
      return theme.textColor.success.toString();
    default:
      Logger.warn(`Unknown color: ${color}. Returning primary color as fallback.`);
      return theme.textColor.primary.toString();
  }
}

export function withOpacity(color: string, opacity: number): string {
  const percent = Math.round(opacity * 100);
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

export function throwOrLogError(errorText: string): void {
  const error = new Error(errorText);
  if (isProdNodeEnv()) {
    Logger.error(error);
  } else {
    throw error;
  }
}
